// packages/ui/src/forms/fields/_styles.ts
import { alpha, type SxProps, type Theme } from '@mui/material/styles';

export function getUnsetFieldSx(isUnset: boolean): SxProps<Theme> | undefined {
  if (!isUnset) {
    return undefined;
  }

  return {
    '& .MuiOutlinedInput-root, & .MuiPickersOutlinedInput-root': {
      backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
    },
  };
}

/**
 * Combined background fill for shared form inputs. Resolves the
 * three-way decision (red highlight > white fill > transparent) in
 * one place so every field component composes the same precedence:
 *
 *   1. `highlightUnset` → red empty-required tint (always wins).
 *   2. `background === 'white'` → paint `background.paper` so the
 *      input reads as solid white instead of bleeding through any
 *      tinted parent surface (MUI outlined inputs are transparent
 *      by default).
 *   3. `background === 'transparent'` → no fill; the input picks up
 *      whatever sits behind it.
 *
 * White is the default because most of our forms now sit on tinted
 * cards; the few callers that genuinely want bare-MUI transparency
 * opt out via `background="transparent"`.
 */
export function getFieldFillSx(
  highlightUnset: boolean,
  background: 'white' | 'transparent' = 'white',
): SxProps<Theme> | undefined {
  if (highlightUnset) {
    return {
      '& .MuiOutlinedInput-root, & .MuiPickersOutlinedInput-root': {
        backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
      },
    };
  }
  if (background === 'white') {
    return {
      '& .MuiOutlinedInput-root, & .MuiPickersOutlinedInput-root': {
        backgroundColor: 'background.paper',
      },
    };
  }
  return undefined;
}

/* ───────────────────────────────────────────────────────────────────────────
 * Shared `leftIcon`-row layout — single source of truth.
 *
 * The visual "icon → input" spacing of every shared form input is composed
 * from these two constants. Tweak them HERE and every input updates in
 * lockstep — no per-component sx edits, no drift.
 *
 *   Icon column width      = SHARED_ICON_BOX_WIDTH_PX (40px)
 *   Gap to input           = SHARED_FIELD_ROW_GAP    (theme spacing units)
 *
 * Total icon-to-input horizontal offset = 40 + gap-px.
 *
 * Inputs render as:
 *
 *   <Box sx={sharedFieldRowSx()}>          ← outer flex + gap
 *     <Box sx={sharedLeftIconBoxSx()}>...</Box>  ← fixed-width icon column
 *     <Box sx={{ flex: 1 }}>...</Box>      ← actual input
 *   </Box>
 *
 * Inputs that span multiple rows (PhoneInput, FormYesNoRadioGroup,
 * LodgingSearchInput) override `alignItems` via `sx={[sharedFieldRowSx(),
 * { alignItems: 'flex-start' }]}` — same gap, different vertical
 * alignment.
 *
 * NEVER hand-roll the icon-row layout (`<Box sx={{ display: 'flex',
 * gap: ... }}>` + `<Box sx={{ ... p: 1, ... }}>{leftIcon}</Box>`) in a
 * shared input. Use these helpers so everything stays aligned and a
 * single edit can adjust the look across the whole product.
 * ─────────────────────────────────────────────────────────────────────── */

/**
 * Default `maxWidth` (in px) for shared form fields when no override is
 * passed in via `sx`. Keeps select / number / text inputs from stretching
 * the full width of their container — a single dropdown spanning 900px
 * looks broken next to a label that's 200px wide. Callers that genuinely
 * want a wider field override via `sx={{ maxWidth: ... }}` (or `'none'`).
 */
export const SHARED_FIELD_MAX_WIDTH_PX = 360;

/** Fixed pixel width of the leftIcon column. Pinned regardless of icon size. */
export const SHARED_ICON_BOX_WIDTH_PX = 40;

/** Horizontal gap between the icon column and the input, in MUI theme spacing units (1 = 8px). */
export const SHARED_FIELD_ROW_GAP = 1;

/**
 * Total left offset (px) from the row's left edge to where the input
 * actually starts when an icon is shown. Used by callers that need to
 * align an extra element (e.g. PhonesInput's "Add phone" button) with
 * the input column.
 */
export const SHARED_INPUT_LEFT_OFFSET_PX =
  SHARED_ICON_BOX_WIDTH_PX + SHARED_FIELD_ROW_GAP * 8;

/**
 * Canonical outer-flex sx for a shared input row. Defaults to
 * `alignItems: 'center'` (single-line inputs); multi-row inputs override
 * via array sx: `sx={[sharedFieldRowSx(), { alignItems: 'flex-start' }]}`.
 *
 * Exposed as a getter (rather than a typed const) so the inferred literal
 * type flows through MUI's `Box.sx` prop and the `sx={[...]}` array form
 * without tripping `@typescript-eslint/no-unsafe-assignment`.
 */
export const sharedFieldRowSx = () => ({
  display: 'flex' as const,
  alignItems: 'center' as const,
  gap: SHARED_FIELD_ROW_GAP,
});

/**
 * Canonical sx for the small Box that wraps a `leftIcon`. The fixed
 * `width` makes the icon column reserve identical horizontal space
 * regardless of which icon is passed in (medium 24px vs small 20px),
 * so input fields stack with their left edges aligned to the pixel.
 */
export const sharedLeftIconBoxSx = () => ({
  color: 'text.secondary' as const,
  display: 'flex' as const,
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
  flexShrink: 0,
  width: SHARED_ICON_BOX_WIDTH_PX,
  p: 1,
});

/** The height of an outlined input's box by its `size`: MUI's own, which the theme keeps. */
const OUTLINED_INPUT_HEIGHT_PX = { small: 40, medium: 56 } as const;

/**
 * Merged onto `sharedLeftIconBoxSx` when the field is taller than its input box (helper text under it, several
 * lines): the icon is centred on the box, at the top of the row. Centred on the whole field, it sat below the box,
 * beside the helper text.
 */
export const sharedLeftIconOnInputSx = (size: 'small' | 'medium' = 'medium') => ({
  alignSelf: 'flex-start' as const,
  boxSizing: 'border-box' as const,
  height: OUTLINED_INPUT_HEIGHT_PX[size],
});

/** The size `sharedLeftIconOnInputSx` takes, from a TextField's `size` (unset is MUI's medium). */
export const outlinedInputSize = (size: string | undefined): 'small' | 'medium' => (size === 'small' ? 'small' : 'medium');
